import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TableWindow from '../../../../components/Table/TableWindow';
import useTablePages from '../../../../hooks/useTablePages';
import availableColumns from './Plans.columns';
import { Flex } from 'rebass';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import PageBar from '../../../../components/PageBar/PageBar';
import { RouteComponentProps, withRouter } from 'react-router';
import { PortalWithState } from 'react-portal';
import useMode from '../../../../hooks/useMode';
import FilterBarPlans from './FilterBarPlans';
import Button from '../../../../components/Button/Button';
import Layout from '../../../../components/Layout/Layout';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import {
  GET_PLATFORM_PLAN_IDS,
  GET_PLATFORM_PLANS,
  PlatformPlan,
} from './Plans.query';
import PlanEditModal from './PlanEditModal';

type PlanListProps = RouteComponentProps;

/**
 * @component Used for managing platform plans in the admin interface
 */
function Plans({ match }: PlanListProps): JSX.Element {
  const mode = useMode();
  const [filters, setFilters] = useState<Record<string, unknown> | null>({});
  const [selectedPlan, setSelectedPlan] = useState<PlatformPlan | null>(null);

  const { orderBy, onHeaderClick } = useOrderBy(
    'plans',
    availableColumns,
    'name'
  );

  const { loading, itemIds, selectedIds, setSelectedIds, itemLoader, refetch } =
    useTablePages<PlatformPlan>(
      'platformPlans',
      GET_PLATFORM_PLAN_IDS,
      {
        where: filters || {},
        limit: null,
        orderBy,
      },
      GET_PLATFORM_PLANS,
      (ids) => ({
        where: {
          id: { in: ids },
        },
        limit: null,
        orderBy,
      })
    );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'plans');

  return (
    <Layout>
      <Flex
        flexGrow={1}
        flexDirection="column"
        px="mainContent"
        pb="mainContent"
      >
        <PageBar title="Plans">
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  ml={3}
                  variant="primary"
                  icon="plus"
                  onClick={openPortal}
                >
                  Add Plan
                </Button>
                {portal(
                  <PlanEditModal
                    action="add"
                    onClose={closePortal}
                    refetchQueries={[GET_PLATFORM_PLAN_IDS]}
                  />
                )}
              </>
            )}
          </PortalWithState>
        </PageBar>

        <FilterBarPlans filters={filters || {}} setFilters={setFilters} />

        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <>
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => {
                const handleClick = (plan: PlatformPlan) => {
                  setSelectedPlan(plan);
                  openPortal();
                };

                return (
                  <>
                    <TableWindow
                      columns={visibleColumns}
                      itemIds={itemIds}
                      itemLoader={itemLoader}
                      onSelectionChange={setSelectedIds}
                      selectedIds={selectedIds}
                      onHeaderClick={onHeaderClick}
                      orderBy={orderBy}
                      onRowClick={handleClick}
                      columnConfiguration={columnConfiguration}
                      setColumnConfiguration={setColumnConfiguration}
                      showActionBar={false}
                    />
                    {portal(
                      <PlanEditModal
                        action="edit"
                        plan={selectedPlan}
                        onClose={closePortal}
                        // refetchQueries={[GET_PLATFORM_PLAN_IDS]}
                      />
                    )}
                  </>
                );
              }}
            </PortalWithState>
          </>
        )}
      </Flex>
    </Layout>
  );
}

Plans.propTypes = {
  match: PropTypes.any,
};

export default withRouter(Plans);
